import { useState, useEffect } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { 
  getBootstrapData, 
  clearBootstrapData, 
  isBootstrapValid,
  isOffline 
} from '@/lib/offline-bootstrap';

interface UseAuthReturn {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isOfflineMode: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<{ error: Error | null }>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
  updatePassword: (newPassword: string) => Promise<{ error: Error | null }>;
}

export function useAuth(): UseAuthReturn {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isOfflineMode, setIsOfflineMode] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const initializeAuth = async () => {
      try {
        // Set up auth state listener FIRST
        const { data: { subscription } } = supabase.auth.onAuthStateChange(
          (event, session) => {
            if (!isMounted) return;
            setSession(session);
            setUser(session?.user ?? null);
            setIsOfflineMode(false); // Online session means not in offline mode
            setLoading(false);
          }
        );

        // Try to get session from Supabase
        const { data: { session }, error } = await supabase.auth.getSession();

        if (!isMounted) {
          subscription.unsubscribe();
          return;
        }

        if (session) {
          // Online session available
          setSession(session);
          setUser(session.user);
          setIsOfflineMode(false);
          setLoading(false);
        } else if (error || isOffline()) {
          // Network error or offline - try bootstrap cache
          if (isBootstrapValid()) {
            const bootstrapData = getBootstrapData();
            if (bootstrapData) {
              console.log('[useAuth] Offline mode: Using bootstrap cache');
              // Create a minimal mock user for offline mode
              const mockUser: User = {
                id: bootstrapData.userId,
                email: bootstrapData.userEmail,
                app_metadata: {},
                user_metadata: { full_name: bootstrapData.userName },
                aud: 'authenticated',
                created_at: '',
              };
              setUser(mockUser);
              setSession(null); // No valid session, but we have user data
              setIsOfflineMode(true);
              setLoading(false);
            } else {
              setLoading(false);
            }
          } else {
            // No valid bootstrap cache
            setLoading(false);
          }
        } else {
          // No session and not offline
          setLoading(false);
        }

        return () => {
          isMounted = false;
          subscription.unsubscribe();
        };
      } catch (error) {
        console.error('[useAuth] Error initializing auth:', error);
        
        // On error, try offline bootstrap
        if (isBootstrapValid()) {
          const bootstrapData = getBootstrapData();
          if (bootstrapData && isMounted) {
            console.log('[useAuth] Error recovery: Using bootstrap cache');
            const mockUser: User = {
              id: bootstrapData.userId,
              email: bootstrapData.userEmail,
              app_metadata: {},
              user_metadata: { full_name: bootstrapData.userName },
              aud: 'authenticated',
              created_at: '',
            };
            setUser(mockUser);
            setSession(null);
            setIsOfflineMode(true);
          }
        }
        
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    initializeAuth();

    return () => {
      isMounted = false;
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signUp = async (email: string, password: string, fullName: string) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName,
        },
      },
    });
    return { error };
  };

  const signOut = async () => {
    // Clear bootstrap data on logout to prevent stale data
    clearBootstrapData();
    setIsOfflineMode(false);
    
    const { error } = await supabase.auth.signOut();
    return { error };
  };

  const resetPassword = async (email: string) => {
    const redirectUrl = `${window.location.origin}/reset-password`;
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl,
    });
    return { error };
  };

  const updatePassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });
    return { error };
  };

  return {
    user,
    session,
    loading,
    isOfflineMode,
    signIn,
    signUp,
    signOut,
    resetPassword,
    updatePassword,
  };
}
